import { useEffect, useState } from 'react'
import { useStore } from '../core/store'
import { landingLine, startLine } from '../core/announce'
import type { Op } from '../core/types'

/** How recent a pass must be to be said as started when it arrives with a new
 *  document: the first question's opening and the worked example's replay are
 *  born with their exploration, while a document opened from the list carries
 *  only passes nobody here just pressed. */
const JUST_STARTED_MS = 5000

/**
 * The page's one polite announcer: a pass has started, a pass has landed, and
 * what the record says it did.
 *
 * Always mounted, and mounted OUTSIDE the app (`boot.tsx`, beside the
 * durability notice) for the reason the page's h1 sits outside `main.stage`: a
 * sheet makes the stage `inert`, and a live region inside an inert subtree is
 * silent — so a pass that landed while the palette was open would never be
 * heard.
 *
 * It watches ops, not nodes: an op that this page saw start (no `finishedAt`)
 * and now sees finished is one landing, however many nodes it streamed in on
 * the way. An op that arrives already finished — a restored exploration, an
 * undo, a file opened — was not watched landing and says nothing. The sentence
 * itself is `landingLine`'s, built from the stored record only.
 *
 * The start is said too (`startLine`): a press left a screen reader in silence
 * for the whole of a pass, since nothing under focus changes when one begins. A
 * pass that starts and lands inside one beat — a refusal, a local outcome — is
 * said once, as its landing. Landings are read before starts, which is the
 * order Up next runs them in: the pass in front lands, then the next begins.
 *
 * Quiet while a run is MOVING: those passes are the run's, and its pod is a
 * live region of its own that already speaks each move. A pass the reader fires
 * by hand while a run waits at its gate is theirs and is announced — the same
 * line the spend toast draws in `endOp`. Quiet under the first-run sheet too,
 * where the worked example unfurls with nobody's press.
 */
export function Announcer() {
  const [line, setLine] = useState('')

  useEffect(() => {
    const running = new Set<string>()
    let sessionId = ''
    let timer: ReturnType<typeof setTimeout> | null = null
    let landed: string[] = []
    let started: string[] = []
    // A repeated sentence must still be spoken — two Deepens that both land
    // three claims on one node are two landings — and a live region only speaks
    // on a change, so identical lines alternate a trailing no-break space.
    let flip = false

    const watch = (ops: readonly Op[], id: string) => {
      const moving = useStore.getState().run?.phase === 'moving'
      if (id !== sessionId) {
        // Another document, or the worked example reminted under its own id by
        // the pass now starting: nothing it already holds was seen landing. A
        // pass it was born with is said as starting (`JUST_STARTED_MS`).
        sessionId = id
        running.clear()
        started = []
        for (const o of ops) {
          if (o.finishedAt !== undefined) continue
          running.add(o.id)
          if (!moving && Date.now() - o.startedAt < JUST_STARTED_MS) started.push(o.id)
        }
      } else {
        for (const o of ops) {
          if (o.finishedAt === undefined) {
            if (running.has(o.id)) continue
            running.add(o.id)
            if (!moving) started.push(o.id)
          } else if (running.delete(o.id)) {
            if (moving) continue
            landed.push(o.id)
          }
        }
      }
      if ((landed.length || started.length) && timer === null) {
        // A beat later, not inside `endOp`: a source check writes its record
        // just AFTER its op settles (in the same task), and the sentence reads
        // that record.
        timer = setTimeout(speak, 250)
      }
    }

    const speak = () => {
      timer = null
      // Quiet under the first-run sheet. The only pass that runs there is the
      // worked example unfurling behind it, which nobody pressed, and the page
      // said "Branch started on …" as it loaded, over the sheet's own question.
      // Asked here, a beat after, because boot starts the unfurl in the same
      // tick that raises the sheet, just before it.
      if (useStore.getState().setupOpen === 'splash') {
        landed = []
        started = []
        return
      }
      const s = useStore.getState().session
      const ops = (ids: string[]) =>
        ids.map((id) => s.ops.find((o) => o.id === id)).filter((o): o is Op => !!o)
      const lines = [
        ...ops(landed).map((o) => landingLine(s, o)),
        // A pass that already landed in this beat is said by its landing alone.
        ...ops(started.filter((id) => !landed.includes(id))).map((o) => startLine(s, o)),
      ].filter((l): l is string => !!l)
      landed = []
      started = []
      if (!lines.length) return
      flip = !flip
      setLine(lines.join(' ') + (flip ? ' ' : ''))
    }

    const first = useStore.getState().session
    sessionId = first.id
    for (const o of first.ops) if (o.finishedAt === undefined) running.add(o.id)
    const unsub = useStore.subscribe((s, prev) => {
      if (s.session.ops !== prev.session.ops || s.session.id !== prev.session.id) {
        watch(s.session.ops, s.session.id)
      }
    })
    return () => {
      unsub()
      if (timer !== null) clearTimeout(timer)
    }
  }, [])

  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {line}
    </div>
  )
}
